import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { of as observableOf, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { FlowInstanceService } from "../services/flow-instance.service";
import { LoaderService } from '../../common/services/loader.service';

import { genId } from "../../core/services/genId";
import { FlowInstance, StateType } from "../models/flow.model";
import { WorkflowStates } from '../../common/models/flow.dto';
import { WorkOrderStatus, WorkOrderType } from '../../common/models/work-order.model';

@Injectable()
export class WorkflowResolver  {

  public resolveResponse: FlowInstance | HttpErrorResponse;
  public workflow: FlowInstance;
  public workItemId: any;

  constructor(
    public flowInstanceService: FlowInstanceService,
    public loader: LoaderService,
    public router: Router
  ) { }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): FlowInstance | Observable<FlowInstance | string> | Promise<FlowInstance | string> {
        this.loader.toggleLoader(true);
        const id = route.params.id;
        this.workItemId = (route.params.workItemId) ? route.params.workItemId : null;
        let obs: Observable<FlowInstance>;
        
        if (route.url[0].path === 'create') {
            obs = this.flowInstanceService.create(genId(), id).pipe(catchError((err) => this.handleError(err)));
        } else if (route.url[0].path === 'on-behalf') {
            obs = this.flowInstanceService.getOnBehalf(id).pipe(catchError((err) => this.handleError(err)));
        }        
        else {
            obs = (this.workItemId) ?
                this.flowInstanceService.getByWorkItem(id, this.workItemId).pipe(catchError((err) => this.handleError(err))) :
                this.flowInstanceService.get(id).pipe(catchError((err) => this.handleError(err)));
        }

        return obs.pipe(map((result) => {
          
          this.resolveResponse = result;
          let errorMessage: string;
            
          if (this.resolveResponse instanceof HttpErrorResponse) {
              const error = this.resolveResponse as HttpErrorResponse;
            if (error.status === 403 || error.message.includes('AccessDenied'))
                errorMessage = "You do not have permission to access this resource. Please login or contact your system administrator to continue.";
            else if (error.status === 409)
                errorMessage = "Requested work item is not available for activation";           
            else
              errorMessage = "An unknown error has occurred";
          } else {
            this.workflow = this.resolveResponse as FlowInstance;
            if (this.workflow.currentState && this.workflow.currentState.toLowerCase() === WorkflowStates.Cancelled.toLowerCase())
              errorMessage = `This workflow has been ${WorkflowStates.Cancelled}`;

            let state = this.workflow.findCurrentState();
            if (!state)
              throw new Error("workflow has no / invalid current state");

            if (!this.workflow.context.sections)
              this.workflow.context.sections = []; //TODO: Look at this to see if it can be done differently
            if (!this.workflow.context.navigations) 
                this.workflow.context.navigations = [];
            this.loader.toggleLoader(false);
            const accessMessage = this.validateAccess(this.workItemId, this.workflow.findCurrentState());
            if (accessMessage)
              return accessMessage;
          }
          if (errorMessage)
            return errorMessage;
          return this.workflow;
        }, (error) => {
          this.loader.toggleLoader(false);
          const msg = error as HttpErrorResponse;
          if (msg.status === 403 || error.message.includes('AccessDenied'))
            return "You do not have permission to access this resource. Please login or contact your system administrator to continue.";
          else
            return "An unknown error has occurred";
        }));
  }

  public handleError(error: any): Observable<any> {
    return observableOf(error);
  }

  public validateAccess(workItemId: string, state: any): string {
    if (state.stateType === StateType.ReviewForm && !workItemId)
      return "Workflow is currently awaiting completion of one or more work items";
    else if (!this.workflow.associatedWorkItems && workItemId)
      return "Requested work item is not associated with this workflow";
    else if (workItemId && this.workflow.associatedWorkItems && this.workflow.associatedWorkItems.length > 0) {
      const requestedWorkItem = this.workflow.associatedWorkItems.find(wi => wi.id === workItemId);
      if (!requestedWorkItem)
        return "Requested work item is not associated with this workflow";
      else if (requestedWorkItem.status === WorkOrderStatus.Completed)
        return "Requested work item has already been completed.";
      else {
        const workItem = this.workflow.associatedWorkItems.find(wi => wi.id === this.workflow.currentWorkItem);
        if (workItem && this.workflow.currentWorkItem !== workItemId && requestedWorkItem.type !== WorkOrderType.AdHocReview)
          return "Requested work item is not available for activation";
      }
    }
  }

}
